import type {
  SortingState,
  FilteringState,
  PaginationState,
  GridColumn,
} from '../types';
import { getNestedValue, stableSort } from '../utils';

export function paginateData<T>(data: T[], pagination: PaginationState): T[] {
  const { pageIndex, pageSize } = pagination;
  const startIndex = pageIndex * pageSize;
  const endIndex = startIndex + pageSize;

  return data.slice(startIndex, endIndex);
}

export function sortData<T>(data: T[], sorting: SortingState[]): T[] {
  if (!sorting || sorting.length === 0) {
    return [...data];
  }

  const compareFunction = (a: T, b: T): number => {
    for (const sort of sorting) {
      const aValue = getNestedValue(a, sort.id);
      const bValue = getNestedValue(b, sort.id);

      if (aValue == null && bValue == null) continue;
      if (aValue == null) return -1;
      if (bValue == null) return 1;

      let result = 0;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        result = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        result = aValue.getTime() - bValue.getTime();
      } else {
        result = String(aValue).localeCompare(String(bValue));
      }

      if (sort.desc) {
        result = result * -1;
      }

      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };

  return stableSort(data, compareFunction);
}

export function filterData<T>(data: T[], filtering: FilteringState): T[] {
  if (!filtering || Object.keys(filtering).length === 0) {
    return [...data];
  }

  return data.filter((row) => {
    return Object.entries(filtering).every(([columnId, filterValue]) => {
      if (!filterValue || filterValue === '') return true;

      const cellValue = getNestedValue(row, columnId);

      const cellStr = String(cellValue || '').toLowerCase();
      const filterStr = String(filterValue).toLowerCase();

      return cellStr.includes(filterStr);
    });
  });
}

export function processGridData<T>(
  originalData: T[],
  sorting: SortingState[],
  filtering: FilteringState,
  pagination: PaginationState
): {
  processedData: T[];
  totalCount: number;
  filteredCount: number;
} {
  const filtered = filterData(originalData, filtering);
  const sorted = sortData(filtered, sorting);
  const paginated = paginateData(sorted, pagination);

  return {
    processedData: paginated,
    totalCount: originalData.length,
    filteredCount: filtered.length,
  };
}

export function getValueType(value: any): 'number' | 'date' | 'string' {
  if (typeof value === 'number') return 'number';
  if (value instanceof Date) return 'date';
  if (typeof value === 'string' && !isNaN(Date.parse(value))) return 'date';
  if (typeof value === 'string' && !isNaN(Number(value))) return 'number';
  return 'string';
}

export function compareValues(
  a: any,
  b: any,
  type: 'number' | 'date' | 'string'
): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  switch (type) {
    case 'number':
      return Number(a) - Number(b);
    case 'date':
      return new Date(a).getTime() - new Date(b).getTime();
    case 'string':
    default:
      return String(a).localeCompare(String(b));
  }
}

export function validateCellValue<T>(
  value: any,
  column: GridColumn<T>,
  item: T
): string | null {
  const { validation } = column;

  if (!validation) return null;

  if (validation.required && (value == null || value === '')) {
    return '필수 입력 항목입니다';
  }

  if (typeof value === 'string') {
    if (validation.minLength && value.length < validation.minLength) {
      return `최소 ${validation.minLength}자 이상 입력해주세요`;
    }
    if (validation.maxLength && value.length > validation.maxLength) {
      return `최대 ${validation.maxLength}자까지 입력 가능합니다`;
    }
  }

  if (typeof value === 'number') {
    if (validation.min !== undefined && value < validation.min) {
      return `최솟값은 ${validation.min}입니다`;
    }
    if (validation.max !== undefined && value > validation.max) {
      return `최댓값은 ${validation.max}입니다`;
    }
  }

  if (validation.pattern && typeof value === 'string') {
    if (!validation.pattern.test(value)) {
      return '올바른 형식이 아닙니다';
    }
  }

  if (validation.custom) {
    const customError = validation.custom(value, item);
    if (customError) {
      return customError;
    }
  }

  return null;
}

export function shouldValidateOnEvent<T>(
  column: GridColumn<T>,
  eventType: 'realtime' | 'blur' | 'submit'
): boolean {
  const { validation } = column;
  if (!validation?.validateOn) return eventType === 'blur';

  return (
    validation.validateOn === eventType ||
    (validation.validateOn === 'both' &&
      (eventType === 'realtime' || eventType === 'blur'))
  );
}

export function shouldShowErrorOnEvent<T>(
  column: GridColumn<T>,
  eventType: 'input' | 'blur' | 'submit'
): boolean {
  const { validation } = column;
  if (!validation?.showErrorOn) return eventType === 'blur';

  return validation.showErrorOn === eventType;
}

export function validateRowData<T>(
  item: T,
  columns: GridColumn<T>[]
): Record<string, string> {
  const errors: Record<string, string> = {};

  columns.forEach((column) => {
    const value = getNestedValue(item, column.accessorKey as string);
    const error = validateCellValue(value, column, item);
    if (error) {
      errors[column.id] = error;
    }
  });

  return errors;
}
